import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Users, MessageSquare, AlertTriangle, Trash2, Ban, Star, ChevronLeft, ChevronRight, Mail, Eye, UserCog, Crown, Search, BookOpen, ToggleLeft, ToggleRight, ExternalLink, FileText, Plus, Edit, X, MapPin, Plane, Route, Building2, Check, Save, ImageIcon, Sparkles, Loader2 } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { API } from '../config';
import PhotoPickerDialog from '../components/admin/PhotoPickerDialog';
import AdsManager from '../components/admin/AdsManager';

/** Debounce hook — returns a debounced copy of `value` */
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function Pagination({ page, pages, onPageChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="pagination">
      <span className="text-xs text-muted-foreground">Page {page} of {pages}</span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft className="w-3.5 h-3.5" />
        </Button>
        {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
          const p = pages <= 5 ? i + 1 : Math.max(1, Math.min(page - 2, pages - 4)) + i;
          return (
            <Button key={p} size="sm" variant={p === page ? 'default' : 'outline'} className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-[#FF4500] hover:bg-[#E03E00]' : ''}`} onClick={() => onPageChange(p)}>
              {p}
            </Button>
          );
        })}
        <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
          <ChevronRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  // Paginated data
  const [usersData, setUsersData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [postsData, setPostsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [reportsData, setReportsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [contactsData, setContactsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [blogsData, setBlogsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [routesData, setRoutesData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [userSearch, setUserSearch] = useState('');
  const [blogSearch, setBlogSearch] = useState('');
  const [routeSearch, setRouteSearch] = useState('');

  // Airports state
  const [airportsData, setAirportsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [airportSearch, setAirportSearch] = useState('');
  const [airportEditOpen, setAirportEditOpen] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [airportForm, setAirportForm] = useState({
    name: '', description: '', crowd_level: 'medium', image_url: '',
    amenities: '', airlines: '', terminals: '', wifi: false, lounge: false, prayer_room: false,
  });
  const [savingAirport, setSavingAirport] = useState(false);

  // Route editor state
  const [routeEditOpen, setRouteEditOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({ description: '', featured: false, image_url: '' });
  const [savingRoute, setSavingRoute] = useState(false);

  // Create route state
  const [createRouteOpen, setCreateRouteOpen] = useState(false);
  const [newRouteForm, setNewRouteForm] = useState({ origin_code: '', destination_code: '', description: '' });
  const [creatingRoute, setCreatingRoute] = useState(false);

  // Route posts (moderation)
  const [routePostsData, setRoutePostsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [postsFilter, setPostsFilter] = useState('airport'); // 'airport' | 'route'

  // Photo picker state
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [photoPickerQuery, setPhotoPickerQuery] = useState('');
  const [photoPickerCallback, setPhotoPickerCallback] = useState(null);

  // Route suggestions
  const [routeSuggestions, setRouteSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [addingRoute, setAddingRoute] = useState(null); // route_id being added

  // Airport suggestions
  const [airportSuggestions, setAirportSuggestions] = useState([]);
  const [showAirportSuggestions, setShowAirportSuggestions] = useState(false);
  const [loadingAirportSuggestions, setLoadingAirportSuggestions] = useState(false);
  const [addingAirport, setAddingAirport] = useState(null); // iata_code being added

  // Search-ready guard (prevent debounce firing on initial mount)
  const searchReady = useRef(false);
  // Blog editor state
  const [blogEditorOpen, setBlogEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null); // null = create, object = edit
  const [blogForm, setBlogForm] = useState({
    title: '', slug: '', excerpt: '', content: '',
    cover_image: '', tags: '', published: false, read_time: '',
  });
  const [blogPreview, setBlogPreview] = useState(false);
  const [savingBlog, setSavingBlog] = useState(false);

  // AI blog generation state
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ topic: '', tone: 'informative', extra_notes: '' });
  const [generatingBlog, setGeneratingBlog] = useState(false);

  // Dialog state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) navigate('/');
  }, [user, loading, navigate]);

  // ── Debounced search values ────────────────────────────────────────────
  const dUserSearch    = useDebounce(userSearch,    400);
  const dBlogSearch    = useDebounce(blogSearch,    400);
  const dRouteSearch   = useDebounce(routeSearch,   400);
  const dAirportSearch = useDebounce(airportSearch, 400);

  // Auto-search when debounced values change (skip initial mount)
  useEffect(() => { if (searchReady.current) fetchUsers(1, dUserSearch);    }, [dUserSearch]);    // eslint-disable-line
  useEffect(() => { if (searchReady.current) fetchBlogs(1, dBlogSearch);    }, [dBlogSearch]);    // eslint-disable-line
  useEffect(() => { if (searchReady.current) fetchRoutes(1, dRouteSearch);  }, [dRouteSearch]);   // eslint-disable-line
  useEffect(() => { if (searchReady.current) fetchAirports(1, dAirportSearch); }, [dAirportSearch]); // eslint-disable-line

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(res.data);
    } catch {}
  }, []);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      setUsersData(res.data);
    } catch {}
  }, []);

  const fetchPosts = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/posts`, { params: { page, limit: 15 }, withCredentials: true });
      setPostsData(res.data);
    } catch {}
  }, []);

  const fetchReports = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/reports`, { params: { page, limit: 15 }, withCredentials: true });
      setReportsData(res.data);
    } catch {}
  }, []);

  const fetchContacts = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/contacts`, { params: { page, limit: 15 }, withCredentials: true });
      setContactsData(res.data);
    } catch {}
  }, []);

  const fetchBlogs = useCallback(async (page = 1, search = '') => {
    try {
      const params = { page, limit: 15 };
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/blogs`, { params, withCredentials: true });
      setBlogsData(res.data);
    } catch {}
  }, []);

  const fetchRoutes = useCallback(async (page = 1, search = '') => {
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/routes`, { params, withCredentials: true });
      setRoutesData(res.data);
    } catch {}
  }, []);

  const fetchAirports = useCallback(async (page = 1, search = '') => {
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/airports`, { params, withCredentials: true });
      setAirportsData(res.data);
    } catch {}
  }, []);

  const fetchRoutePosts = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/route-posts`, { params: { page, limit: 20 }, withCredentials: true });
      setRoutePostsData(res.data);
    } catch {}
  }, []);

  const fetchRouteSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/admin/route-suggestions`, { withCredentials: true });
      setRouteSuggestions(res.data.suggestions || []);
    } catch {}
    finally { setLoadingSuggestions(false); }
  }, []);

  const fetchAirportSuggestions = useCallback(async () => {
    setLoadingAirportSuggestions(true);
    try {
      const res = await axios.get(`${API}/admin/airport-suggestions`, { withCredentials: true });
      setAirportSuggestions(res.data.suggestions || []);
    } catch {}
    finally { setLoadingAirportSuggestions(false); }
  }, []);

  const handleAddSuggestedAirport = async (suggestion) => {
    setAddingAirport(suggestion.iata_code);
    try {
      await axios.post(`${API}/admin/airports`, suggestion, { withCredentials: true });
      toast.success(`${suggestion.iata_code} — ${suggestion.city} added!`);
      setAirportSuggestions(prev => prev.filter(s => s.iata_code !== suggestion.iata_code));
      fetchAirports(1, airportSearch);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add airport');
    } finally { setAddingAirport(null); }
  };

  const openPhotoPicker = (query, onSelect) => {
    setPhotoPickerQuery(query);
    setPhotoPickerCallback(() => onSelect);
    setPhotoPickerOpen(true);
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    Promise.all([fetchStats(), fetchUsers(), fetchPosts(), fetchReports(), fetchContacts(), fetchBlogs(), fetchRoutes(), fetchAirports(), fetchRoutePosts()])
      .finally(() => {
        setLoadingData(false);
        // Allow debounced search effects to fire from now on
        setTimeout(() => { searchReady.current = true; }, 100);
      });
  }, [user, fetchStats, fetchUsers, fetchPosts, fetchReports, fetchContacts, fetchBlogs, fetchRoutes, fetchAirports, fetchRoutePosts]);

  const handleTogglePremium = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-premium`, {}, { withCredentials: true });
      setUsersData(prev => ({ ...prev, items: prev.items.map(u => u.user_id === userId ? { ...u, is_premium: res.data.is_premium } : u) }));
      if (selectedUser?.user_id === userId) setSelectedUser(prev => ({ ...prev, is_premium: res.data.is_premium }));
      toast.success(res.data.is_premium ? 'Premium activated' : 'Premium removed');
      fetchStats();
    } catch { toast.error('Failed to toggle premium'); }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role }, { withCredentials: true });
      setUsersData(prev => ({ ...prev, items: prev.items.map(u => u.user_id === userId ? { ...u, role } : u) }));
      if (selectedUser?.user_id === userId) setSelectedUser(prev => ({ ...prev, role }));
      toast.success(`Role changed to ${role}`);
    } catch { toast.error('Failed to change role'); }
  };

  const handleBanUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/ban`, {}, { withCredentials: true });
      setUsersData(prev => ({ ...prev, items: prev.items.map(u => u.user_id === userId ? { ...u, banned: true } : u) }));
      if (selectedUser?.user_id === userId) setSelectedUser(prev => ({ ...prev, banned: true }));
      toast.success('User banned');
    } catch { toast.error('Failed'); }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/unban`, {}, { withCredentials: true });
      setUsersData(prev => ({ ...prev, items: prev.items.map(u => u.user_id === userId ? { ...u, banned: false } : u) }));
      if (selectedUser?.user_id === userId) setSelectedUser(prev => ({ ...prev, banned: false }));
      toast.success('User unbanned');
    } catch { toast.error('Failed'); }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setPostsData(prev => ({ ...prev, items: prev.items.filter(p => p.post_id !== postId), total: prev.total - 1 }));
      toast.success('Post deleted');
    } catch { toast.error('Failed'); }
  };

  const handleUpdateReport = async (reportId, status) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}`, { status }, { withCredentials: true });
      setReportsData(prev => ({ ...prev, items: prev.items.map(r => r.report_id === reportId ? { ...r, status } : r) }));
      toast.success('Report updated');
    } catch { toast.error('Failed'); }
  };

  const handleUpdateContact = async (contactId, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}`, { status }, { withCredentials: true });
      setContactsData(prev => ({ ...prev, items: prev.items.map(c => c.contact_id === contactId ? { ...c, status } : c) }));
      if (selectedContact?.contact_id === contactId) setSelectedContact(prev => ({ ...prev, status }));
      toast.success(`Marked as ${status}`);
      fetchStats();
    } catch { toast.error('Failed'); }
  };

  const handleToggleBlogPublish = async (postId) => {
    try {
      const res = await axios.patch(`${API}/admin/blogs/${postId}/toggle-publish`, {}, { withCredentials: true });
      setBlogsData(prev => ({ ...prev, items: prev.items.map(b => b.post_id === postId ? { ...b, published: res.data.published } : b) }));
      if (selectedBlog?.post_id === postId) setSelectedBlog(prev => ({ ...prev, published: res.data.published }));
      toast.success(res.data.published ? 'Blog post published' : 'Blog post unpublished');
    } catch { toast.error('Failed to toggle publish status'); }
  };

  const handleDeleteBlog = async (postId) => {
    try {
      await axios.delete(`${API}/admin/blogs/${postId}`, { withCredentials: true });
      setBlogsData(prev => ({ ...prev, items: prev.items.filter(b => b.post_id !== postId), total: prev.total - 1 }));
      setBlogDialogOpen(false);
      toast.success('Blog post deleted');
      fetchStats();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteRoute = async (routeId) => {
    try {
      await axios.delete(`${API}/admin/routes/${routeId}`, { withCredentials: true });
      setRoutesData(prev => ({ ...prev, items: prev.items.filter(r => r.route_id !== routeId), total: prev.total - 1 }));
      toast.success('Route deleted');
      fetchStats();
    } catch { toast.error('Failed to delete route'); }
  };

  const handleCreateRoute = async () => {
    if (!newRouteForm.origin_code.trim() || !newRouteForm.destination_code.trim()) {
      toast.error('Both airport codes required'); return;
    }
    setCreatingRoute(true);
    try {
      await axios.post(`${API}/admin/routes`, {
        origin_code: newRouteForm.origin_code.toUpperCase(),
        destination_code: newRouteForm.destination_code.toUpperCase(),
        description: newRouteForm.description,
      }, { withCredentials: true });
      toast.success('Route created!');
      setCreateRouteOpen(false);
      setNewRouteForm({ origin_code: '', destination_code: '', description: '' });
      fetchRoutes(1, routeSearch);
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create route'); }
    finally { setCreatingRoute(false); }
  };

  const openRouteEditor = (route) => {
    setEditingRoute(route);
    setRouteForm({
      description: route.description || '',
      featured: !!route.featured,
      image_url: route.image_url || '',
    });
    setRouteEditOpen(true);
  };

  const handleSaveRoute = async () => {
    if (!editingRoute) return;
    setSavingRoute(true);
    try {
      const res = await axios.put(`${API}/admin/routes/${editingRoute.route_id}`, routeForm, { withCredentials: true });
      setRoutesData(prev => ({ ...prev, items: prev.items.map(r => r.route_id === editingRoute.route_id ? { ...r, ...res.data } : r) }));
      toast.success('Route updated!');
      setRouteEditOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update route'); }
    finally { setSavingRoute(false); }
  };

  const openAirportEditor = (airport) => {
    setEditingAirport(airport);
    setAirportForm({
      name: airport.name || '',
      description: airport.description || '',
      crowd_level: airport.crowd_level || 'medium',
      image_url: airport.image_url || '',
      amenities: (airport.amenities || []).join(', '),
      airlines: (airport.airlines || []).join(', '),
      terminals: (airport.terminals || []).join(', '),
      wifi: !!airport.wifi,
      lounge: !!airport.lounge,
      prayer_room: !!airport.prayer_room,
    });
    setAirportEditOpen(true);
  };

  const handleSaveAirport = async () => {
    if (!editingAirport) return;
    setSavingAirport(true);
    try {
      const payload = {
        ...airportForm,
        amenities: airportForm.amenities.split(',').map(s => s.trim()).filter(Boolean),
        airlines: airportForm.airlines.split(',').map(s => s.trim()).filter(Boolean),
        terminals: airportForm.terminals.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await axios.put(`${API}/admin/airports/${editingAirport.iata_code}`, payload, { withCredentials: true });
      setAirportsData(prev => ({ ...prev, items: prev.items.map(a => a.iata_code === editingAirport.iata_code ? { ...a, ...res.data } : a) }));
      toast.success('Airport updated!');
      setAirportEditOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update airport'); }
    finally { setSavingAirport(false); }
  };

  const handleDeleteRoutePost = async (postId) => {
    try {
      await axios.delete(`${API}/admin/route-posts/${postId}`, { withCredentials: true });
      setRoutePostsData(prev => ({ ...prev, items: prev.items.filter(p => p.post_id !== postId), total: prev.total - 1 }));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete post'); }
  };

  const handleAddSuggestedRoute = async (suggestion) => {
    setAddingRoute(suggestion.route_id);
    try {
      await axios.post(`${API}/admin/routes`, {
        origin_code: suggestion.origin_code,
        destination_code: suggestion.destination_code,
      }, { withCredentials: true });
      toast.success(`Route ${suggestion.route_id} created!`);
      setRouteSuggestions(prev => prev.filter(s => s.route_id !== suggestion.route_id));
      fetchRoutes(1, routeSearch);
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create route');
    } finally { setAddingRoute(null); }
  };

  const handleViewBlog = async (postId) => {
    try {
      const res = await axios.get(`${API}/admin/blogs/${postId}`, { withCredentials: true });
      setSelectedBlog(res.data);
      setBlogDialogOpen(true);
    } catch { toast.error('Failed to load post'); }
  };

  const openBlogEditor = async (post = null) => {
    if (post) {
      // Fetch full post (including content) before editing
      try {
        const res = await axios.get(`${API}/admin/blogs/${post.post_id}`, { withCredentials: true });
        const full = res.data;
        setEditingPost(full);
        setBlogForm({
          title: full.title || '',
          slug: full.slug || '',
          excerpt: full.excerpt || '',
          content: full.content || '',
          cover_image: full.cover_image || '',
          tags: (full.tags || []).join(', '),
          published: !!full.published,
          read_time: full.read_time || '',
        });
      } catch {
        toast.error('Could not load post for editing');
        return;
      }
    } else {
      setEditingPost(null);
      setBlogForm({ title: '', slug: '', excerpt: '', content: '', cover_image: '', tags: '', published: false, read_time: '' });
    }
    setBlogPreview(false);
    setBlogEditorOpen(true);
  };

  const autoSlug = (title) => {
    return title.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
  };

  const handleBlogFormChange = (field, value) => {
    setBlogForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'title' && !editingPost) next.slug = autoSlug(value);
      if (field === 'content' && !next.read_time) next.read_time = Math.max(1, Math.round(value.split(/\s+/).length / 200));
      return next;
    });
  };

  const handleSaveBlog = async (publish = null) => {
    if (!blogForm.title.trim()) { toast.error('Title is required'); return; }
    setSavingBlog(true);
    const payload = {
      ...blogForm,
      tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      published: publish !== null ? publish : blogForm.published,
      read_time: parseInt(blogForm.read_time) || Math.max(1, Math.round(blogForm.content.split(/\s+/).length / 200)),
    };
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blogs/${editingPost.post_id}/edit`, payload, { withCredentials: true });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/admin/blogs/create`, payload, { withCredentials: true });
        toast.success(payload.published ? 'Post published!' : 'Draft saved!');
      }
      setBlogEditorOpen(false);
      fetchBlogs(1, blogSearch);
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingBlog(false); }
  };

  const handleAIGenerate = async () => {
    if (!aiForm.topic.trim()) { toast.error('Enter a topic to generate'); return; }
    setGeneratingBlog(true);
    try {
      const res = await axios.post(`${API}/admin/blogs/ai-generate`, aiForm, { withCredentials: true });
      const gen = res.data;
      setBlogForm(prev => ({
        ...prev,
        title: gen.title || prev.title,
        content: gen.content || prev.content,
        excerpt: gen.excerpt || prev.excerpt,
        tags: (gen.tags || []).join(', '),
        slug: gen.slug || prev.slug,
        read_time: gen.word_count ? String(Math.max(1, Math.round(gen.word_count / 200))) : prev.read_time,
      }));
      setAiPanelOpen(false);
      toast.success(`Generated ${gen.word_count || '~1200'} words!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setGeneratingBlog(false); }
  };

  if (loading || loadingData) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        <Navbar />
        <main className="flex-1"><div className="max-w-6xl mx-auto px-4 py-8 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div></main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Navbar />
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-[#FF4500]" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }} data-testid="admin-title">Admin Dashboard</h1>
          </div>

          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
              {[
                ['Users', stats.total_users, Users],
                ['Airports', stats.total_airports, MapPin],
                ['Routes', stats.total_routes, null],
                ['Posts', stats.total_posts, MessageSquare],
                ['Premium', stats.premium_users, Crown],
                ['Contacts', stats.new_contacts, Mail],
                ['Reports', stats.pending_reports, AlertTriangle],
                ['Flights', stats.total_flights, Plane],
              ].map(([label, val, Icon]) => (
                <Card key={label} className="fly-card p-3 text-center">
                  <div className="text-2xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{val ?? 0}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </Card>
              ))}
            </div>
          )}

          <Tabs defaultValue="users">
            <div className="overflow-x-auto -mx-4 px-4 mb-4 scrollbar-hide">
              <TabsList className="w-max flex-nowrap gap-1 h-auto">
                <TabsTrigger value="users" data-testid="admin-tab-users">Users ({usersData.total})</TabsTrigger>
                <TabsTrigger value="contacts" data-testid="admin-tab-contacts">Contacts ({contactsData.total})</TabsTrigger>
                <TabsTrigger value="moderation" data-testid="admin-tab-moderation">Moderation</TabsTrigger>
                <TabsTrigger value="blogs" data-testid="admin-tab-blogs">Blog ({blogsData.total})</TabsTrigger>
                <TabsTrigger value="routes" data-testid="admin-tab-routes">Routes ({routesData.total})</TabsTrigger>
                <TabsTrigger value="airports" data-testid="admin-tab-airports">Airports ({airportsData.total})</TabsTrigger>
                <TabsTrigger value="ads" data-testid="admin-tab-ads">Ads</TabsTrigger>
                <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports ({reportsData.total})</TabsTrigger>
              </TabsList>
            </div>

            {/* ── USERS TAB ───────────────────────────────────── */}
            <TabsContent value="users">
              <Card className="fly-card overflow-hidden">
                <div className="p-3 border-b border-border">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search users…" className="pl-8 pr-8 h-8 text-sm" value={userSearch}
                      onChange={e => setUserSearch(e.target.value)} data-testid="admin-user-search" />
                    {userSearch && (
                      <button onClick={() => setUserSearch('')} className="absolute right-2.5 top-2 text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">User</TableHead>
                      <TableHead className="w-[200px]">Email</TableHead>
                      <TableHead className="w-[90px]">Role</TableHead>
                      <TableHead className="w-[90px]">Premium</TableHead>
                      <TableHead className="w-[60px]">XP</TableHead>
                      <TableHead className="w-[80px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usersData.items.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No users found</TableCell></TableRow>
                    ) : usersData.items.map(u => (
                      <TableRow key={u.user_id} className={u.banned ? 'opacity-50' : ''}>
                        <TableCell className="w-[200px]">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7 shrink-0"><AvatarImage src={u.avatar} /><AvatarFallback>{(u.name || '?')[0]}</AvatarFallback></Avatar>
                            <span className="text-sm font-medium truncate">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="w-[200px] text-xs text-muted-foreground">
                          <span className="truncate block max-w-[190px]">{u.email}</span>
                        </TableCell>
                        <TableCell className="w-[90px]">
                          <Badge className={`text-[10px] ${u.role === 'admin' ? 'bg-[#FF4500]/15 text-[#FF4500]' : ''}`}>{u.role}</Badge>
                        </TableCell>
                        <TableCell className="w-[90px]">
                          {u.is_premium ? <Badge className="text-[10px] bg-amber-500/15 text-amber-600">Premium</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="w-[60px] text-xs">{u.xp ?? 0}</TableCell>
                        <TableCell className="w-[80px] text-right">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setSelectedUser(u); setUserDialogOpen(true); }} data-testid={`admin-view-user-${u.user_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                <Pagination page={usersData.page} pages={usersData.pages} onPageChange={p => fetchUsers(p, userSearch)} />
              </Card>
            </TabsContent>

            {/* ── CONTACTS TAB ────────────────────────────────── */}
            <TabsContent value="contacts">
              <Card className="fly-card overflow-hidden">
                <div className="overflow-x-auto">
                <Table className="min-w-[640px] table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[140px]">Name</TableHead>
                      <TableHead className="w-[180px]">Email</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead className="w-[90px]">Status</TableHead>
                      <TableHead className="w-[110px]">Date</TableHead>
                      <TableHead className="w-[80px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactsData.items.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No contact requests</TableCell></TableRow>
                    ) : contactsData.items.map(c => (
                      <TableRow key={c.contact_id}>
                        <TableCell className="text-sm font-medium truncate">{c.name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <span className="truncate block max-w-[170px]">{c.email}</span>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="truncate block max-w-[220px]">{c.subject}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${c.status === 'new' ? 'bg-blue-500/15 text-blue-600' : c.status === 'replied' ? 'bg-green-500/15 text-green-600' : 'bg-zinc-500/15 text-zinc-500'}`}>
                            {c.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setSelectedContact(c); setContactDialogOpen(true); }} data-testid={`admin-view-contact-${c.contact_id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                <Pagination page={contactsData.page} pages={contactsData.pages} onPageChange={p => fetchContacts(p)} />
              </Card>
            </TabsContent>

            {/* ── MODERATION TAB ───────────────────────────────── */}
            <TabsContent value="moderation">
              {/* Filter toggle */}
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => setPostsFilter('airport')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${postsFilter === 'airport' ? 'bg-[#FF4500] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  data-testid="filter-airport-posts"
                >
                  <MapPin className="w-3.5 h-3.5 inline mr-1.5" />Airport Posts ({postsData.total})
                </button>
                <button
                  onClick={() => { setPostsFilter('route'); fetchRoutePosts(1); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${postsFilter === 'route' ? 'bg-[#FF4500] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                  data-testid="filter-route-posts"
                >
                  <Plane className="w-3.5 h-3.5 inline mr-1.5" />Route Posts ({routePostsData.total})
                </button>
              </div>

              {postsFilter === 'airport' ? (
                <Card className="fly-card overflow-hidden">
                  <div className="overflow-x-auto">
                  <Table className="min-w-[560px] table-fixed w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">Author</TableHead>
                        <TableHead>Content</TableHead>
                        <TableHead className="w-[90px]">Airport</TableHead>
                        <TableHead className="w-[90px]">Type</TableHead>
                        <TableHead className="w-[80px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {postsData.items.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No posts</TableCell></TableRow>
                      ) : postsData.items.map(p => (
                        <TableRow key={p.post_id}>
                          <TableCell className="text-sm font-medium truncate">{p.author_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <span className="truncate block max-w-full">{p.content}</span>
                          </TableCell>
                          <TableCell><Badge className="text-[10px]">{p.airport_code || '—'}</Badge></TableCell>
                          <TableCell><Badge className="text-[10px] capitalize">{p.post_type?.replace('_', ' ') || '—'}</Badge></TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove the post.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeletePost(p.post_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  <Pagination page={postsData.page} pages={postsData.pages} onPageChange={p => fetchPosts(p)} />
                </Card>
              ) : (
                <Card className="fly-card overflow-hidden">
                  <div className="overflow-x-auto">
                  <Table className="min-w-[560px] table-fixed w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[150px]">Author</TableHead>
                        <TableHead>Content</TableHead>
                        <TableHead className="w-[100px]">Route</TableHead>
                        <TableHead className="w-[80px]">Type</TableHead>
                        <TableHead className="w-[80px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {routePostsData.items.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No route posts</TableCell></TableRow>
                      ) : routePostsData.items.map(p => (
                        <TableRow key={p.post_id}>
                          <TableCell className="text-sm font-medium truncate">{p.author_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <span className="truncate block max-w-full">{p.content}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className="text-[10px] font-mono bg-[#FF4500]/15 text-[#FF4500]">{p.route_id || '—'}</Badge>
                          </TableCell>
                          <TableCell><Badge className="text-[10px] capitalize">{p.post_type?.replace('_', ' ') || '—'}</Badge></TableCell>
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this route post?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove the post from the route community.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteRoutePost(p.post_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                  <Pagination page={routePostsData.page} pages={routePostsData.pages} onPageChange={p => fetchRoutePosts(p)} />
                </Card>
              )}
            </TabsContent>

            {/* ── BLOGS TAB ─────────────────────────────────── */}
            <TabsContent value="blogs">
              <Card className="fly-card overflow-hidden">
                <div className="p-3 border-b border-border flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search blog posts…"
                      className="pl-8 pr-8 h-8 text-sm"
                      value={blogSearch}
                      onChange={e => setBlogSearch(e.target.value)}
                      data-testid="blog-search-input"
                    />
                    {blogSearch && (
                      <button onClick={() => setBlogSearch('')} className="absolute right-2.5 top-2 text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <Button size="sm" className="h-8 text-xs fly-btn-primary ml-auto" onClick={() => openBlogEditor()} data-testid="create-blog-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Create Post
                  </Button>
                </div>
                <div className="overflow-x-auto">
                <Table className="min-w-[700px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead className="w-[160px]">Tags</TableHead>
                      <TableHead className="w-[70px]">Views</TableHead>
                      <TableHead className="w-[70px]">Likes</TableHead>
                      <TableHead className="w-[90px]">Status</TableHead>
                      <TableHead className="w-[120px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blogsData.items.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No blog posts found</TableCell></TableRow>
                    ) : blogsData.items.map(b => (
                      <TableRow key={b.post_id} className={!b.published ? 'opacity-60' : ''} data-testid={`blog-row-${b.post_id}`}>
                        <TableCell>
                          {b.cover_image ? (
                            <img src={b.cover_image} alt="" className="w-8 h-8 rounded object-cover" loading="lazy" onError={e => e.target.style.display='none'} />
                          ) : (
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium line-clamp-1">{b.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[260px]">{b.excerpt}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(b.tags || []).slice(0, 2).map(t => (
                              <Badge key={t} className="text-[9px] px-1.5 py-0">{t}</Badge>
                            ))}
                            {(b.tags || []).length > 2 && (
                              <span className="text-[9px] text-muted-foreground">+{b.tags.length - 2}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{b.views ?? 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{(b.likes || []).length}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] cursor-pointer ${b.published ? 'bg-green-500/15 text-green-600' : 'bg-zinc-500/15 text-zinc-500'}`}
                            onClick={() => handleToggleBlogPublish(b.post_id)}
                            title="Click to toggle"
                            data-testid={`blog-status-${b.post_id}`}>
                            {b.published ? 'Published' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleViewBlog(b.post_id)} data-testid={`blog-view-${b.post_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[#FF4500]" onClick={() => openBlogEditor(b)} data-testid={`blog-edit-${b.post_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-7 px-2 ${b.published ? 'text-amber-500' : 'text-green-600'}`}
                              onClick={() => handleToggleBlogPublish(b.post_id)}
                              title={b.published ? 'Unpublish' : 'Publish'}
                              data-testid={`blog-toggle-${b.post_id}`}
                            >
                              {b.published ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete blog post?</AlertDialogTitle>
                                  <AlertDialogDescription>"{b.title}" will be permanently deleted. This cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteBlog(b.post_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                <Pagination page={blogsData.page} pages={blogsData.pages} onPageChange={p => fetchBlogs(p, blogSearch)} />
              </Card>
            </TabsContent>

            {/* ── ROUTES TAB ────────────────────────────────── */}
            <TabsContent value="routes">
              <Card className="fly-card overflow-hidden">
                <div className="p-3 border-b border-border flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search routes by code (e.g. DXB, LHR)…"
                      className="pl-8 pr-8 h-8 text-sm"
                      value={routeSearch}
                      onChange={e => setRouteSearch(e.target.value)}
                      data-testid="route-search-admin"
                    />
                    {routeSearch && (
                      <button onClick={() => setRouteSearch('')} className="absolute right-2.5 top-2 text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <Button size="sm" className="h-8 text-xs fly-btn-primary" onClick={() => setCreateRouteOpen(true)} data-testid="create-route-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Create Route
                  </Button>
                  <Button
                    size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                    onClick={() => { setShowSuggestions(v => !v); if (!showSuggestions && routeSuggestions.length === 0) fetchRouteSuggestions(); }}
                    data-testid="route-suggestions-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    {showSuggestions ? 'Hide Suggestions' : 'Suggested Routes'}
                  </Button>
                  <a href="/routes" target="_blank" rel="noopener noreferrer"
                    className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Browse Routes
                  </a>
                </div>

                {/* ── Route suggestions panel ── */}
                {showSuggestions && (
                  <div className="p-4 border-b border-border bg-amber-50/40 dark:bg-amber-900/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <p className="text-sm font-semibold">Suggested Routes to Add</p>
                      <p className="text-xs text-muted-foreground">— popular routes not yet in your database</p>
                    </div>
                    {loadingSuggestions ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading suggestions…
                      </div>
                    ) : routeSuggestions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">All popular routes are already in your database!</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {routeSuggestions.map(s => (
                          <div key={s.route_id} className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-border bg-background hover:border-[#FF4500]/40 transition-colors">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 text-xs font-mono font-bold text-[#FF4500]">
                                {s.origin_airport?.flag} {s.origin_code}
                                <span className="text-muted-foreground font-normal">→</span>
                                {s.destination_airport?.flag} {s.destination_code}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                                {s.origin_airport?.city} → {s.destination_airport?.city}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              className="h-6 px-2 text-[10px] fly-btn-primary shrink-0"
                              onClick={() => handleAddSuggestedRoute(s)}
                              disabled={addingRoute === s.route_id}
                              data-testid={`add-suggestion-${s.route_id}`}
                            >
                              {addingRoute === s.route_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table className="min-w-[680px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px]">Route</TableHead>
                        <TableHead>Origin</TableHead>
                        <TableHead>Destination</TableHead>
                        <TableHead className="w-[80px]">Flyers</TableHead>
                        <TableHead className="w-[70px]">Posts</TableHead>
                        <TableHead className="w-[70px]">Featured</TableHead>
                        <TableHead className="w-[140px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {routesData.items.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No routes found</TableCell></TableRow>
                      ) : routesData.items.map(r => (
                        <TableRow key={r.route_id} data-testid={`admin-route-row-${r.route_id}`}>
                          <TableCell>
                            <span className="font-mono text-sm font-semibold text-[#FF4500]">{r.route_id}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {r.origin_airport?.flag && <span>{r.origin_airport.flag}</span>}
                              <div>
                                <p className="text-sm font-medium">{r.origin_code}</p>
                                <p className="text-xs text-muted-foreground">{r.origin_airport?.city || '—'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {r.destination_airport?.flag && <span>{r.destination_airport.flag}</span>}
                              <div>
                                <p className="text-sm font-medium">{r.destination_code}</p>
                                <p className="text-xs text-muted-foreground">{r.destination_airport?.city || '—'}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{r.checkin_count ?? 0}</TableCell>
                          <TableCell className="text-sm">{r.post_count ?? 0}</TableCell>
                          <TableCell>
                            {r.featured ? <Badge className="text-[10px] bg-amber-500/15 text-amber-600">Featured</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" className="h-7 px-2"
                                onClick={() => window.open(`/route/${r.route_id}`, '_blank')}
                                title="View community">
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-[#FF4500]"
                                onClick={() => openRouteEditor(r)}
                                title="Edit route"
                                data-testid={`admin-route-edit-${r.route_id}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete route {r.route_id}?</AlertDialogTitle>
                                    <AlertDialogDescription>This removes the route community and all its data. Cannot be undone.</AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteRoute(r.route_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination page={routesData.page} pages={routesData.pages} onPageChange={p => fetchRoutes(p, routeSearch)} />
              </Card>
            </TabsContent>

            {/* ── AIRPORTS TAB ─────────────────────────────── */}
            <TabsContent value="airports">
              <Card className="fly-card overflow-hidden">
                <div className="p-3 border-b border-border flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by code, city, country…"
                      className="pl-8 pr-8 h-8 text-sm"
                      value={airportSearch}
                      onChange={e => setAirportSearch(e.target.value)}
                      data-testid="airport-search-admin"
                    />
                    {airportSearch && (
                      <button onClick={() => setAirportSearch('')} className="absolute right-2.5 top-2 text-muted-foreground hover:text-foreground">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <Button
                    size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                    onClick={() => {
                      setShowAirportSuggestions(v => !v);
                      if (!showAirportSuggestions && airportSuggestions.length === 0) fetchAirportSuggestions();
                    }}
                    data-testid="airport-suggestions-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    {showAirportSuggestions ? 'Hide Suggestions' : 'Suggested Airports'}
                  </Button>
                </div>

                {/* ── Airport suggestions panel ── */}
                {showAirportSuggestions && (
                  <div className="p-4 border-b border-border bg-amber-50/40 dark:bg-amber-900/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <p className="text-sm font-semibold">Suggested Airports to Add</p>
                      <p className="text-xs text-muted-foreground">— major global airports not yet in your database</p>
                    </div>
                    {loadingAirportSuggestions ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading suggestions…
                      </div>
                    ) : airportSuggestions.length === 0 ? (
                      <p className="text-sm text-muted-foreground">All suggested airports are already in your database!</p>
                    ) : (
                      <>
                        {/* Group by region */}
                        {['Europe','Americas','Africa','Asia','Oceania'].map(region => {
                          const items = airportSuggestions.filter(s => s.region === region);
                          if (!items.length) return null;
                          return (
                            <div key={region} className="mb-4">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{region}</p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                                {items.map(s => (
                                  <div key={s.iata_code} className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-border bg-background hover:border-[#FF4500]/40 transition-colors">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1 text-xs font-mono font-bold text-[#FF4500]">
                                        {s.flag} {s.iata_code}
                                      </div>
                                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">{s.city}, {s.country}</p>
                                    </div>
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-[10px] fly-btn-primary shrink-0"
                                      onClick={() => handleAddSuggestedAirport(s)}
                                      disabled={addingAirport === s.iata_code}
                                      data-testid={`add-airport-${s.iata_code}`}
                                    >
                                      {addingAirport === s.iata_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table className="min-w-[720px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[70px]">Code</TableHead>
                        <TableHead>Airport</TableHead>
                        <TableHead className="w-[120px]">City / Country</TableHead>
                        <TableHead className="w-[90px]">Crowd</TableHead>
                        <TableHead className="w-[70px]">Posts</TableHead>
                        <TableHead className="w-[120px]">Facilities</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {airportsData.items.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No airports found</TableCell></TableRow>
                      ) : airportsData.items.map(a => (
                        <TableRow key={a.iata_code} data-testid={`admin-airport-row-${a.iata_code}`}>
                          <TableCell>
                            <span className="font-mono font-bold text-[#FF4500]">{a.iata_code}</span>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{a.name}</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{a.city}</p>
                            <p className="text-xs text-muted-foreground">{a.country}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] capitalize ${a.crowd_level === 'low' ? 'bg-green-500/15 text-green-600' : a.crowd_level === 'high' ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-600'}`}>
                              {a.crowd_level || 'medium'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{a.post_count ?? 0}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {a.wifi && <Badge className="text-[9px] px-1 py-0 bg-blue-500/10 text-blue-600">WiFi</Badge>}
                              {a.lounge && <Badge className="text-[9px] px-1 py-0 bg-purple-500/10 text-purple-600">Lounge</Badge>}
                              {a.prayer_room && <Badge className="text-[9px] px-1 py-0 bg-teal-500/10 text-teal-600">Prayer</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="ghost" className="h-7 px-2"
                                onClick={() => window.open(`/airport/${a.iata_code}`, '_blank')}
                                title="View page">
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-[#FF4500]"
                                onClick={() => openAirportEditor(a)}
                                title="Edit airport"
                                data-testid={`admin-airport-edit-${a.iata_code}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination page={airportsData.page} pages={airportsData.pages} onPageChange={p => fetchAirports(p, airportSearch)} />
              </Card>
            </TabsContent>

            {/* ── ADS TAB ──────────────────────────────────── */}
            <TabsContent value="ads">
              <AdsManager />
            </TabsContent>

            {/* ── REPORTS TAB ─────────────────────────────────── */}
            <TabsContent value="reports">
              <Card className="fly-card overflow-hidden">
                <div className="overflow-x-auto">
                <Table className="min-w-[480px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="w-[90px]">Status</TableHead>
                      <TableHead className="w-[140px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsData.items.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No reports</TableCell></TableRow>
                    ) : reportsData.items.map(r => (
                      <TableRow key={r.report_id}>
                        <TableCell><Badge className="text-[10px]">{r.target_type}</Badge></TableCell>
                        <TableCell className="text-sm">
                          <span className="truncate block max-w-[280px]">{r.reason}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] ${r.status === 'pending' ? 'bg-amber-500/15 text-amber-600' : 'bg-green-500/15 text-green-600'}`}>{r.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateReport(r.report_id, 'resolved')}>Resolve</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-500" onClick={() => handleUpdateReport(r.report_id, 'dismissed')}>Dismiss</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
                <Pagination page={reportsData.page} pages={reportsData.pages} onPageChange={p => fetchReports(p)} />
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />

      {/* ── USER DETAIL DIALOG ──────────────────────────── */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-[#FF4500]" />
              Manage User
            </DialogTitle>
            <DialogDescription>View and update user settings</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12"><AvatarImage src={selectedUser.avatar} /><AvatarFallback>{(selectedUser.name || '?')[0]}</AvatarFallback></Avatar>
                <div>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold text-[#FF4500]">{selectedUser.xp ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">XP</p>
                </div>
                <div className="p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold">{selectedUser.flight_count ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground">Flights</p>
                </div>
                <div className="p-2 rounded-lg bg-muted/50">
                  <p className="text-lg font-bold">{selectedUser.rank || 'N/A'}</p>
                  <p className="text-[10px] text-muted-foreground">Rank</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Role</span>
                  <Select value={selectedUser.role} onValueChange={v => handleChangeRole(selectedUser.user_id, v)} disabled={selectedUser.user_id === user?.user_id}>
                    <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="admin-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="moderator">Moderator</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Premium Status</span>
                  <Button size="sm" variant={selectedUser.is_premium ? 'default' : 'outline'}
                    className={`h-8 text-xs ${selectedUser.is_premium ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                    onClick={() => handleTogglePremium(selectedUser.user_id)}
                    data-testid="admin-toggle-premium">
                    <Crown className="w-3.5 h-3.5 mr-1" />
                    {selectedUser.is_premium ? 'Remove Premium' : 'Grant Premium'}
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Account Status</span>
                  {selectedUser.user_id !== user?.user_id ? (
                    selectedUser.banned ? (
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleUnbanUser(selectedUser.user_id)} data-testid="admin-unban-user">
                        Unban User
                      </Button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="h-8 text-xs text-red-500" data-testid="admin-ban-user">
                            <Ban className="w-3.5 h-3.5 mr-1" /> Ban User
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Ban {selectedUser.name}?</AlertDialogTitle>
                            <AlertDialogDescription>This will prevent the user from accessing the platform. You can unban them later.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleBanUser(selectedUser.user_id)} className="bg-red-500 hover:bg-red-600">Ban</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )
                  ) : <Badge className="text-xs">Current Admin</Badge>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── CONTACT DETAIL DIALOG ───────────────────────── */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="admin-contact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#FF4500]" />
              Contact Request
            </DialogTitle>
            <DialogDescription>Review and respond to this inquiry</DialogDescription>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="text-sm font-medium">{selectedContact.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium">{selectedContact.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm">{selectedContact.created_at ? new Date(selectedContact.created_at).toLocaleString() : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge className={`text-[10px] ${selectedContact.status === 'new' ? 'bg-blue-500/15 text-blue-600' : selectedContact.status === 'replied' ? 'bg-green-500/15 text-green-600' : 'bg-zinc-500/15 text-zinc-500'}`}>
                    {selectedContact.status}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Subject</p>
                <p className="text-sm font-medium">{selectedContact.subject}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Message</p>
                <div className="p-3 rounded-lg bg-muted/50 text-sm whitespace-pre-wrap">{selectedContact.message}</div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs" onClick={() => handleUpdateContact(selectedContact.contact_id, 'archived')}>Archive</Button>
                <Button size="sm" className="text-xs bg-[#FF4500] hover:bg-[#E03E00]" onClick={() => { handleUpdateContact(selectedContact.contact_id, 'replied'); window.open(`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`); }}>
                  <Mail className="w-3.5 h-3.5 mr-1" /> Reply via Email
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── BLOG DETAIL DIALOG ───────────────────────── */}
      <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-blog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#FF4500]" />
              Blog Post
            </DialogTitle>
            <DialogDescription>Review and manage this post</DialogDescription>
          </DialogHeader>
          {selectedBlog && (
            <div className="space-y-4">
              {selectedBlog.cover_image && (
                <img src={selectedBlog.cover_image} alt={selectedBlog.title} className="w-full h-40 object-cover rounded-xl" />
              )}
              <div>
                <h3 className="font-bold text-lg leading-tight">{selectedBlog.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{selectedBlog.author_name} · {selectedBlog.created_at ? new Date(selectedBlog.created_at).toLocaleDateString() : '—'} · {selectedBlog.read_time || '?'} min read</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {(selectedBlog.tags || []).map(t => (
                  <Badge key={t} className="text-[10px]">{t}</Badge>
                ))}
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                <span>{selectedBlog.views ?? 0} views</span>
                <span>{(selectedBlog.likes || []).length} likes</span>
                <span>{selectedBlog.shares ?? 0} shares</span>
                <Badge className={`text-[10px] ${selectedBlog.published ? 'bg-green-500/15 text-green-600' : 'bg-zinc-500/15 text-zinc-500'}`}>
                  {selectedBlog.published ? 'Published' : 'Draft'}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-muted/40 max-h-48 overflow-y-auto">
                <p className="text-sm text-muted-foreground italic mb-2">{selectedBlog.excerpt}</p>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{selectedBlog.content?.slice(0, 800)}{selectedBlog.content?.length > 800 ? '...' : ''}</div>
              </div>
              <DialogFooter className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="text-xs" onClick={() => window.open(`/blog/${selectedBlog.slug}`, '_blank')} data-testid="blog-view-live">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Live
                </Button>
                <Button
                  size="sm" variant="outline"
                  className={`text-xs ${selectedBlog.published ? 'text-amber-600' : 'text-green-600'}`}
                  onClick={() => handleToggleBlogPublish(selectedBlog.post_id)}
                  data-testid="blog-dialog-toggle"
                >
                  {selectedBlog.published ? <ToggleRight className="w-3.5 h-3.5 mr-1" /> : <ToggleLeft className="w-3.5 h-3.5 mr-1" />}
                  {selectedBlog.published ? 'Unpublish' : 'Publish'}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="text-xs text-red-500" data-testid="blog-dialog-delete">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this blog post?</AlertDialogTitle>
                      <AlertDialogDescription>"{selectedBlog.title}" will be permanently deleted.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDeleteBlog(selectedBlog.post_id)} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── BLOG EDITOR DIALOG ──────────────────────── */}
      <Dialog open={blogEditorOpen} onOpenChange={setBlogEditorOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blog-editor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#FF4500]" />
              {editingPost ? 'Edit Blog Post' : 'Create New Post'}
            </DialogTitle>
            <DialogDescription>Fill in the details below. Use the Preview toggle to check formatting.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* ── AI Generator panel ─────────────── */}
            {!editingPost && (
              <div className="rounded-xl border border-[#7C3AED]/25 overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#7C3AED]/8 to-[#FF4500]/5 hover:from-[#7C3AED]/12 transition-colors"
                  onClick={() => setAiPanelOpen(p => !p)}
                  data-testid="ai-panel-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                    <span className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Generate with AI (Claude Haiku)</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">{aiPanelOpen ? 'Hide ▲' : 'Expand ▼'}</span>
                </button>
                {aiPanelOpen && (
                  <div className="p-4 border-t border-[#7C3AED]/15 bg-background space-y-3">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground block mb-1.5">TOPIC / TITLE *</label>
                      <Input
                        placeholder="e.g. Best Business Class Lounges at Dubai Airport"
                        value={aiForm.topic}
                        onChange={e => setAiForm(p => ({ ...p, topic: e.target.value }))}
                        data-testid="ai-topic-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground block mb-1.5">TONE</label>
                        <Select value={aiForm.tone} onValueChange={v => setAiForm(p => ({ ...p, tone: v }))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['informative', 'casual & friendly', 'adventurous', 'professional', 'inspiring'].map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-muted-foreground block mb-1.5">EXTRA NOTES (optional)</label>
                        <Input
                          placeholder="e.g. focus on budget travelers"
                          value={aiForm.extra_notes}
                          onChange={e => setAiForm(p => ({ ...p, extra_notes: e.target.value }))}
                          className="text-xs"
                        />
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="w-full fly-btn-primary gap-2"
                      onClick={handleAIGenerate}
                      disabled={generatingBlog}
                      data-testid="ai-generate-btn"
                    >
                      {generatingBlog
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating post…</>
                        : <><Sparkles className="w-3.5 h-3.5" /> Generate Post</>
                      }
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center">
                      Generated content will auto-fill the form below. You can edit before publishing.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">TITLE *</label>
              <Input
                placeholder="Enter a compelling post title..."
                value={blogForm.title}
                onChange={e => handleBlogFormChange('title', e.target.value)}
                className="text-base font-semibold"
                data-testid="blog-form-title"
              />
            </div>

            {/* Slug + Read time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">SLUG (URL)</label>
                <Input
                  placeholder="auto-generated-from-title"
                  value={blogForm.slug}
                  onChange={e => handleBlogFormChange('slug', e.target.value)}
                  className="font-mono text-xs"
                  data-testid="blog-form-slug"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">READ TIME (min)</label>
                <Input
                  type="number" min="1" max="60"
                  placeholder="Auto"
                  value={blogForm.read_time}
                  onChange={e => handleBlogFormChange('read_time', e.target.value)}
                />
              </div>
            </div>

            {/* Cover image */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">COVER IMAGE URL</label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://images.unsplash.com/..."
                  value={blogForm.cover_image}
                  onChange={e => handleBlogFormChange('cover_image', e.target.value)}
                  data-testid="blog-form-cover"
                />
                <Button size="sm" variant="outline" className="shrink-0 h-9 text-xs gap-1.5"
                  onClick={() => openPhotoPicker(blogForm.title || 'travel aviation', url => handleBlogFormChange('cover_image', url))}
                  data-testid="blog-browse-photos"
                >
                  <ImageIcon className="w-3.5 h-3.5" /> Browse
                </Button>
                {blogForm.cover_image && (
                  <img src={blogForm.cover_image} alt="" className="w-12 h-9 rounded-lg object-cover flex-shrink-0"
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">TAGS (comma separated)</label>
              <Input
                placeholder="e.g. Dubai, Airport Guide, Emirates, Middle East"
                value={blogForm.tags}
                onChange={e => handleBlogFormChange('tags', e.target.value)}
                data-testid="blog-form-tags"
              />
              {blogForm.tags && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {blogForm.tags.split(',').map(t => t.trim()).filter(Boolean).map(t => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border">{t}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">EXCERPT</label>
              <Textarea
                placeholder="A concise summary that hooks the reader (1-2 sentences)..."
                value={blogForm.excerpt}
                onChange={e => handleBlogFormChange('excerpt', e.target.value)}
                rows={2}
                data-testid="blog-form-excerpt"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-muted-foreground">CONTENT *</label>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setBlogPreview(p => !p)} data-testid="blog-preview-toggle">
                  {blogPreview ? 'Edit' : 'Preview'}
                </Button>
              </div>
              {blogPreview ? (
                <div className="min-h-[200px] p-3 rounded-xl border border-border bg-muted/20 text-sm leading-relaxed whitespace-pre-wrap overflow-y-auto max-h-64">
                  {blogForm.content || <span className="text-muted-foreground italic">Nothing to preview yet...</span>}
                </div>
              ) : (
                <Textarea
                  placeholder={`Write your full blog post here...\nUse ## for headings, **bold**, - for bullet points`}
                  value={blogForm.content}
                  onChange={e => handleBlogFormChange('content', e.target.value)}
                  rows={10}
                  className="font-mono text-xs leading-relaxed resize-y"
                  data-testid="blog-form-content"
                />
              )}
              <p className="text-[10px] text-muted-foreground mt-1">
                {blogForm.content.split(/\s+/).filter(Boolean).length} words
                {blogForm.content ? ` · ~${Math.max(1, Math.round(blogForm.content.split(/\s+/).length / 200))} min read` : ''}
              </p>
            </div>

            {/* Published toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border">
              <div>
                <p className="text-sm font-medium">Publish Status</p>
                <p className="text-xs text-muted-foreground">{blogForm.published ? 'Visible to all users' : 'Draft — not publicly visible'}</p>
              </div>
              <button
                onClick={() => handleBlogFormChange('published', !blogForm.published)}
                className={`relative w-10 h-6 rounded-full transition-colors ${blogForm.published ? 'bg-[#10B981]' : 'bg-muted'}`}
                data-testid="blog-published-toggle"
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${blogForm.published ? 'left-5' : 'left-1'}`} />
              </button>
            </div>
          </div>

          <DialogFooter className="flex gap-2 flex-wrap pt-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setBlogEditorOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => handleSaveBlog(false)} disabled={savingBlog} data-testid="save-draft-btn">
              Save as Draft
            </Button>
            <Button size="sm" className="text-xs fly-btn-primary" onClick={() => handleSaveBlog(true)} disabled={savingBlog} data-testid="publish-blog-btn">
              {savingBlog ? 'Saving...' : editingPost ? 'Update & Publish' : 'Publish Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── ROUTE EDITOR DIALOG ──────────────────────── */}
      <Dialog open={routeEditOpen} onOpenChange={setRouteEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="admin-route-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-[#FF4500]" />
              Edit Route — {editingRoute?.route_id}
            </DialogTitle>
            <DialogDescription>Update route metadata and settings</DialogDescription>
          </DialogHeader>
          {editingRoute && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-muted/40 text-sm">
                <div><p className="text-xs text-muted-foreground">Origin</p><p className="font-bold text-[#FF4500]">{editingRoute.origin_code}</p><p className="text-xs">{editingRoute.origin_airport?.city}</p></div>
                <div><p className="text-xs text-muted-foreground">Destination</p><p className="font-bold text-[#FF4500]">{editingRoute.destination_code}</p><p className="text-xs">{editingRoute.destination_airport?.city}</p></div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">DESCRIPTION</label>
                <Textarea
                  placeholder="Describe this route community..."
                  value={routeForm.description}
                  onChange={e => setRouteForm(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                  data-testid="route-form-description"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">BANNER IMAGE URL (optional)</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://images.unsplash.com/..."
                    value={routeForm.image_url}
                    onChange={e => setRouteForm(p => ({ ...p, image_url: e.target.value }))}
                  />
                  <Button size="sm" variant="outline" className="shrink-0 h-9 text-xs gap-1.5"
                    onClick={() => openPhotoPicker(
                      `${editingRoute?.origin_airport?.city || editingRoute?.origin_code || ''} ${editingRoute?.destination_airport?.city || editingRoute?.destination_code || ''} flight`,
                      url => setRouteForm(p => ({ ...p, image_url: url }))
                    )}
                    data-testid="route-browse-photos"
                  >
                    <ImageIcon className="w-3.5 h-3.5" /> Browse
                  </Button>
                  {routeForm.image_url && (
                    <img src={routeForm.image_url} alt="" className="w-12 h-9 rounded object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border">
                <div>
                  <p className="text-sm font-medium">Featured Route</p>
                  <p className="text-xs text-muted-foreground">Show on Discover &amp; Home pages</p>
                </div>
                <button
                  onClick={() => setRouteForm(p => ({ ...p, featured: !p.featured }))}
                  className={`relative w-10 h-6 rounded-full transition-colors ${routeForm.featured ? 'bg-[#FF4500]' : 'bg-muted'}`}
                  data-testid="route-featured-toggle"
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${routeForm.featured ? 'left-5' : 'left-1'}`} />
                </button>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setRouteEditOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" className="text-xs fly-btn-primary" onClick={handleSaveRoute} disabled={savingRoute} data-testid="save-route-btn">
              <Save className="w-3.5 h-3.5 mr-1" />{savingRoute ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CREATE ROUTE DIALOG ──────────────────────── */}
      <Dialog open={createRouteOpen} onOpenChange={setCreateRouteOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="admin-create-route-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#FF4500]" />
              Create New Route
            </DialogTitle>
            <DialogDescription>Enter the two airport IATA codes to create a route community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">ORIGIN CODE *</label>
                <Input
                  placeholder="e.g. DXB"
                  className="font-mono uppercase"
                  maxLength={3}
                  value={newRouteForm.origin_code}
                  onChange={e => setNewRouteForm(p => ({ ...p, origin_code: e.target.value.toUpperCase() }))}
                  data-testid="new-route-origin"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">DESTINATION CODE *</label>
                <Input
                  placeholder="e.g. LHR"
                  className="font-mono uppercase"
                  maxLength={3}
                  value={newRouteForm.destination_code}
                  onChange={e => setNewRouteForm(p => ({ ...p, destination_code: e.target.value.toUpperCase() }))}
                  data-testid="new-route-destination"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">DESCRIPTION (optional)</label>
              <Textarea
                placeholder="Brief description of this route community..."
                value={newRouteForm.description}
                onChange={e => setNewRouteForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setCreateRouteOpen(false)}>Cancel</Button>
            <Button size="sm" className="text-xs fly-btn-primary" onClick={handleCreateRoute} disabled={creatingRoute} data-testid="submit-create-route">
              {creatingRoute ? 'Creating...' : 'Create Route'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── AIRPORT EDITOR DIALOG ────────────────────── */}
      <Dialog open={airportEditOpen} onOpenChange={setAirportEditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-airport-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#FF4500]" />
              Edit Airport — {editingAirport?.iata_code}
            </DialogTitle>
            <DialogDescription>{editingAirport?.city}, {editingAirport?.country}</DialogDescription>
          </DialogHeader>
          {editingAirport && (
            <div className="space-y-4 pt-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">NAME</label>
                  <Input value={airportForm.name} onChange={e => setAirportForm(p => ({ ...p, name: e.target.value }))} data-testid="airport-form-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">CROWD LEVEL</label>
                  <Select value={airportForm.crowd_level} onValueChange={v => setAirportForm(p => ({ ...p, crowd_level: v }))}>
                    <SelectTrigger data-testid="airport-form-crowd"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">DESCRIPTION</label>
                <Textarea
                  rows={3}
                  value={airportForm.description}
                  onChange={e => setAirportForm(p => ({ ...p, description: e.target.value }))}
                  data-testid="airport-form-description"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">HERO IMAGE URL</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://images.unsplash.com/..."
                    value={airportForm.image_url}
                    onChange={e => setAirportForm(p => ({ ...p, image_url: e.target.value }))}
                    data-testid="airport-form-image"
                  />
                  <Button size="sm" variant="outline" className="shrink-0 h-9 text-xs gap-1.5"
                    onClick={() => openPhotoPicker(`${editingAirport?.city || ''} airport`, url => setAirportForm(p => ({ ...p, image_url: url })))}
                    data-testid="airport-browse-photos"
                  >
                    <ImageIcon className="w-3.5 h-3.5" /> Browse
                  </Button>
                  {airportForm.image_url && (
                    <img src={airportForm.image_url} alt="" className="w-12 h-9 rounded object-cover flex-shrink-0" onError={e => e.target.style.display='none'} />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">AMENITIES (comma-separated)</label>
                  <Textarea rows={2} placeholder="Free Luggage Storage, Currency Exchange..." value={airportForm.amenities} onChange={e => setAirportForm(p => ({ ...p, amenities: e.target.value }))} className="text-xs" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">AIRLINES (comma-separated)</label>
                  <Textarea rows={2} placeholder="Emirates, Qatar Airways..." value={airportForm.airlines} onChange={e => setAirportForm(p => ({ ...p, airlines: e.target.value }))} className="text-xs" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">TERMINALS (comma-separated)</label>
                  <Input placeholder="Terminal 1, Terminal 2" value={airportForm.terminals} onChange={e => setAirportForm(p => ({ ...p, terminals: e.target.value }))} className="text-xs" />
                </div>
              </div>
              <div className="flex gap-6 p-3 rounded-xl bg-muted/30 border border-border flex-wrap">
                {[['wifi', 'WiFi Available'], ['lounge', 'Lounges'], ['prayer_room', 'Prayer Room']].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                    <button
                      onClick={() => setAirportForm(p => ({ ...p, [key]: !p[key] }))}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${airportForm[key] ? 'bg-[#FF4500] border-[#FF4500]' : 'border-border'}`}
                      type="button"
                      data-testid={`airport-form-${key}`}
                    >
                      {airportForm[key] && <Check className="w-2.5 h-2.5 text-white" />}
                    </button>
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setAirportEditOpen(false)}>
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
            <Button size="sm" className="text-xs fly-btn-primary" onClick={handleSaveAirport} disabled={savingAirport} data-testid="save-airport-btn">
              <Save className="w-3.5 h-3.5 mr-1" />{savingAirport ? 'Saving...' : 'Save Airport'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── PHOTO PICKER DIALOG ──────────────────────── */}
      <PhotoPickerDialog
        open={photoPickerOpen}
        onClose={() => setPhotoPickerOpen(false)}
        onSelect={url => photoPickerCallback && photoPickerCallback(url)}
        initialQuery={photoPickerQuery}
      />
    </div>
  );
}